import { Suspense, lazy } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import PrivateRoute from './PrivateRoute'
import PublicRoute from './PublicRoute'
import { AuthContextProvider } from '../context/useAuth'
import { LoadingSpinner } from '@/components/layout/loading-spinner'
import MyInvoices from '@/pages/bo/my-invoices'
import SolutionParticuliers from '@/pages/SolutionParticuliers'
import DownloadLabel from '@/pages/bo/my-orders/[uid]/download-label'

// 懒加载页面组件
const Layout = lazy(() => import('../components/layout/Layout'))
const GuestLayout = lazy(() => import('../components/layout/guest-layout'))
const Home = lazy(() => import('../pages/Home'))
const Login = lazy(() => import('../pages/auth/Login'))
const Signup = lazy(() => import('../pages/auth/Signup'))
const Dashboard = lazy(() => import('../pages/bo/Dashboard'))
const NewDelivery = lazy(() => import('../pages/bo/NewDelivery'))
const NotFound = lazy(() => import('../pages/NotFound'))
const Denied = lazy(() => import('../pages/Denied'))
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'))
const GeneralConditions = lazy(() => import('../pages/GeneralConditions'))
const MyInformation = lazy(() => import('../pages/bo/MyInformation'))
const Credits = lazy(() => import('../pages/bo/credits/index'))
const MyCredits = lazy(() => import('../pages/bo/MyCredits'))
const Simulation = lazy(() => import('../pages/bo/SimulationPage'))
const MyOrders = lazy(() => import('../pages/bo/my-orders/index'))
const Orders = lazy(() => import('../pages/bo/orders/index'))
const Order = lazy(() => import('../pages/bo/orders/[uid]'))
const Providers = lazy(() => import('../pages/bo/providers/index'))
const Provider = lazy(() => import('../pages/bo/providers/[id]'))
const Countries = lazy(() => import('../pages/bo/countries/index'))
const Users = lazy(() => import('../pages/bo/users/index'))
const User = lazy(() => import('../pages/bo/users/[id]'))
const Invoices = lazy(() => import('../pages/bo/invoices/index'))
const Country = lazy(() => import('../pages/bo/countries/[id]'))
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'))
// 加载中组件
const Loading = () => <LoadingSpinner />

// 路由配置
const router = createBrowserRouter([
  // bo路径，使用Layout组件
  {
    path: '/bo',
    element: (
      <AuthContextProvider>
        <PrivateRoute>
          <Suspense fallback={<Loading />}>
            <Layout />
          </Suspense>
        </PrivateRoute>
      </AuthContextProvider>
    ),
    children: [
      // 后台管理路由
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'simulation',
        element: <Simulation />,
      },
      {
        path: 'new-delivery',
        element: <NewDelivery />,
      },
      {
        path: 'orders',
        element: <Orders />,
      },
      {
        path: 'orders/:uid',
        element: <Order />,
      },
      {
        path: 'invoices',
        element: <Invoices />,
      },
      {
        path: 'providers',
        element: <Providers />,
      },
      {
        path: 'providers/:id',
        element: <Provider />,
      },
      {
        path: 'countries',
        element: <Countries />,
      },
      {
        path: 'countries/:id',
        element: <Country />,
      },
      {
        path: 'users',
        element: <Users />,
      },
      {
        path: 'users/:id',
        element: <User />,
      },
      {
        path: 'my-orders',
        element: <MyOrders />,
      },
      {
        path: 'my-invoices',
        element: <MyInvoices />,
      },
      {
        path: 'my-information',
        element: <MyInformation />,
      },
      {
        path: 'credits',
        element: <Credits />,
      },
      {
        path: 'my-credits',
        element: <MyCredits />,
      },
    ],
  },
  // 非bo路径，使用GuestLayout组件
  {
    path: '/',
    element: (
      <AuthContextProvider>
        <PublicRoute>
          <Suspense fallback={<Loading />}>
            <GuestLayout />
          </Suspense>
        </PublicRoute>
      </AuthContextProvider>
    ),
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'my-orders/:id/download-label',
        element: <DownloadLabel />,
      },
      {
        path: 'solution-particuliers',
        element: <SolutionParticuliers />,
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'general-conditions',
        element: <GeneralConditions />,
      },
      {
        path: 'auth/login',
        element: <Login />,
      },
      {
        path: 'auth/signup',
        element: <Signup />,
      },
      {
        path: 'auth/forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'auth/reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'denied',
        element: <Denied />,
      },
      // 404页面
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
])

const AppRouter = () => {
  return <RouterProvider router={router} />
}

export default AppRouter
